import { Link } from "react-router-dom";
import { Play, Download, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ChunkyButton } from "@/components/site/ChunkyButton";
import { Bubbles } from "@/components/site/Bubbles";
import { CHARACTERS, COLORING_BOOK_PDF, CARTOON } from "@/data/characters";

const NameBadge = ({ c, i }) => (
  <motion.div initial={{ opacity: 0, y: 40, scale: 0.6 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: 0.5 + i * 0.15, type: "spring", stiffness: 220, damping: 12 }}>
    <Link to={`/characters/${c.slug}`} data-testid={`welcome-badge-${c.slug}`} className="group flex flex-col items-center">
      <motion.span animate={{ y: [0, -10, 0] }} transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
        className="block h-20 w-20 overflow-hidden rounded-full border-4 bg-white shadow-lg transition-transform group-hover:scale-110 sm:h-28 sm:w-28" style={{ borderColor: c.theme.main }}>
        <img src={c.expressions[0].src} alt={c.name} className="h-full w-full scale-125 object-contain" />
      </motion.span>
      <span className="mt-2 rounded-full px-3 py-1 font-display text-sm font-semibold text-white sm:text-base" style={{ background: c.theme.deep }}>{c.name}</span>
    </Link>
  </motion.div>
);

export const WelcomeScene = () => (
  <section data-testid="hero-section" className="relative overflow-hidden">
    <Bubbles count={10} />
    <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-8 sm:px-8 lg:pt-12">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
        <h1 data-testid="hero-headline" className="logo-text text-4xl leading-tight sm:text-5xl lg:text-7xl">WELCOME TO HANDY & FRIENDS!</h1>
        <p data-testid="hero-tagline" className="mx-auto mt-5 inline-block -rotate-1 rounded-full bg-[#ffe14d] px-6 py-2 font-display text-xl font-semibold text-[#0b4fa3] shadow-sm sm:text-3xl">
          It's Good to Have Clean Hands!
        </p>
      </motion.div>
      <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }} className="relative mx-auto mt-10 max-w-5xl">
        <div className="absolute -inset-4 rounded-[3rem] bg-white/60 blur-2xl" />
        <video data-testid="welcome-scene-video" className="relative aspect-[3/2] w-full rounded-[2.5rem] border-[6px] border-white object-cover shadow-[0_30px_60px_rgba(11,79,163,0.25)]"
          src={CARTOON.loop} poster={CARTOON.poster} autoPlay muted loop playsInline aria-label="Handy, Soapy, Splashy and Germy say hello" />
      </motion.div>
      <div data-testid="welcome-character-badges" className="relative mx-auto mt-8 flex max-w-3xl justify-center gap-4 sm:gap-10">
        {CHARACTERS.map((c, i) => <NameBadge key={c.slug} c={c} i={i} />)}
      </div>
      <div className="mt-12 flex flex-col items-center justify-center gap-5 sm:flex-row sm:flex-wrap">
        <ChunkyButton as="a" href="#watch" data-testid="hero-watch-button">
          <Play className="h-5 w-5 fill-current" /> Watch the Cartoon!
        </ChunkyButton>
        <ChunkyButton as="a" href={COLORING_BOOK_PDF} download data-testid="hero-download-coloring-button" bg="#ffe14d" edge="#d4a600" color="#0b4fa3">
          <Download className="h-5 w-5" /> Download Free Coloring Book
        </ChunkyButton>
        <ChunkyButton data-testid="hero-buy-coloring-button" bg="#ffffff" edge="#bae6fd" color="#0b4fa3"
          onClick={() => toast("Coming soon on Amazon!", { description: "The Handy & Friends coloring book will be available to buy soon." })}>
          <ShoppingCart className="h-5 w-5" /> Buy the Coloring Book
        </ChunkyButton>
      </div>
    </div>
  </section>
);
